import type { TError, TPoll, TPostWithAuthor } from '@onesource/schemas';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

interface VotePollInput {
  pollId: string;
  postId: string;
  optionIds: string[];
}

/**
 * Vote on a poll. Optimistically updates the post's embedded poll in the feed
 * caches (option counts, total, and the user's selection), then reconciles
 * with the authoritative poll returned by the server.
 */
export const useVotePoll = () => {
  const queryClient = useQueryClient();

  // Apply a post-patch function across both infinite and flat post caches.
  const applyToPostCaches = (patchPost: (post: TPostWithAuthor) => TPostWithAuthor) => {
    queryClient.setQueriesData<InfiniteData<{ posts: TPostWithAuthor[]; total: number }>>(
      { queryKey: [ENTITY, 'posts', 'infinite'] },
      old => {
        if (!old) {
          return old;
        }
        return { ...old, pages: old.pages.map(page => ({ ...page, posts: page.posts.map(patchPost) })) };
      },
    );
    queryClient.setQueriesData<TPostWithAuthor[]>(
      { queryKey: [ENTITY, 'posts'], predicate: q => !q.queryKey.includes('infinite') },
      old => (Array.isArray(old) ? old.map(patchPost) : old),
    );
  };

  return useMutation<TPoll, TError, VotePollInput>({
    mutationFn: async ({ pollId, optionIds }) => {
      const res = await axios.post<TPoll>(`/community-hub/polls/${pollId}/vote`, { optionIds });
      return res.data;
    },
    onMutate: async ({ postId, optionIds }) => {
      await queryClient.cancelQueries({ queryKey: [ENTITY, 'posts'] });

      applyToPostCaches(post => {
        if (post.id !== postId || !post.poll) {
          return post;
        }
        const poll = post.poll;
        const wasVoted = new Set(poll.userVotedOptionIds);
        const nowVoted = new Set(optionIds);

        const options = poll.options.map(option => {
          const before = wasVoted.has(option.id);
          const after = nowVoted.has(option.id);
          let { voteCount } = option;
          if (after && !before) {
            voteCount += 1;
          } else if (!after && before) {
            voteCount = Math.max(0, voteCount - 1);
          }
          return { ...option, voteCount };
        });

        const totalVotes = options.reduce((sum, option) => sum + option.voteCount, 0);
        return { ...post, poll: { ...poll, options, totalVotes, userVotedOptionIds: [...nowVoted] } };
      });
    },
    onSuccess: (serverPoll, { postId }) => {
      applyToPostCaches(post => (post.id === postId && post.poll ? { ...post, poll: serverPoll } : post));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY, 'posts'] });
    },
  });
};
